import { ref, type Ref } from 'vue';
import type { DateRange } from '@/types/SearchBar';
import { extendRangeToSaturdayWindow } from '@/utils/date/saturdayWindow';
import { roundUpToWeekly } from '@/utils/filters/durationMode';

/** Whether two dates fall on the same calendar day (timezone-convention safe). */
function sameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Zero-result date-range auto-extension, shared by the product-list and offer
 * filter stores.
 *
 * On a search that returns no results, the date window is widened to a
 * Saturday-to-Saturday range (see {@link extendRangeToSaturdayWindow}) and the
 * `duration` is rounded up to the nearest whole week (multiple of 7), capped at
 * the longest weekly preset (see {@link roundUpToWeekly}). This is what makes
 * hotels selling only fixed Saturday-to-Saturday weekly stays match: their
 * stays come in 1/2/3-week lengths, so a 4- or 8-day request has to be snapped
 * to a weekly length. A duration already longer than the cap is left untouched.
 *
 * Both `dateRange` and `duration` are mutated directly (not via the store
 * setters) so `resetAutoExtend()` is not triggered — using the setters would
 * clear the guard and loop. Both refs change in the same tick, so the store's
 * `apiParams` watcher triggers exactly one follow-up search.
 *
 * `autoExtendAttempted` limits this to one attempt per search. Stores call
 * `resetAutoExtend()` from every search-criterion setter so each new user search
 * gets a fresh attempt.
 *
 * @param dateRange  Store's date-range ref (mutated in place when extending).
 * @param duration   Store's duration ref (rounded up to a weekly length).
 * @param maxWeekly  Getter for the cap: the longest weekly (multiple-of-7)
 *                   duration preset. Kept a getter so it stays reactive to the
 *                   filter-configuration store.
 * @param onExtended Store-specific side effects to run when the window/duration
 *                   actually changes (e.g. mark user interaction, reset pagination).
 */
export function useAutoExtendWindow(
  dateRange: Ref<DateRange>,
  duration: Ref<number>,
  maxWeekly: () => number,
  onExtended?: () => void
) {
  const autoExtendAttempted = ref(false);

  function resetAutoExtend(): void {
    autoExtendAttempted.value = false;
  }

  function applyAutoExtendedWindow(): void {
    // Set the guard first so an already-aligned empty result cannot loop, even
    // on the unchanged short-circuit below.
    autoExtendAttempted.value = true;

    const rounded = roundUpToWeekly(duration.value, maxWeekly());
    const extended = extendRangeToSaturdayWindow(dateRange.value, rounded);
    if (!extended) return;

    const current = dateRange.value;
    const windowUnchanged =
      !!current &&
      sameCalendarDay(current[0], extended[0]) &&
      sameCalendarDay(current[1], extended[1]);
    const durationUnchanged = rounded === duration.value;
    if (windowUnchanged && durationUnchanged) return;

    dateRange.value = extended;
    duration.value = rounded;
    onExtended?.();
  }

  return { autoExtendAttempted, applyAutoExtendedWindow, resetAutoExtend };
}
